/**
 * Frontend contract for the bounded manual-IHI validation outcome block
 * (`ihi_validation`) that the canonical patient create/update API appends when a
 * manually entered IHI candidate was evaluated server-side.
 *
 * B2.2F (HI_010_005819): the authoritative verify/discard decision is made by the
 * authentication service; this module only recognises the bounded, category-only
 * result so the operator can be alerted and the discarded candidate reconciled out
 * of frontend state. The block is response metadata — it is never Patient state.
 *
 * Every field is validated against the fixed server contract: the requirement id,
 * the closed outcome vocabulary, the boolean alert/discard flags and the exact
 * fixed message strings. Anything else fails closed to `null`, so unexpected or
 * malformed response content can never be displayed as trusted IHI feedback, and
 * the parsed result is rebuilt from scratch so planted extra fields (candidates,
 * demographics, provider responses, raw errors, fault text) can never propagate.
 * Nothing in this module logs, and callers must never log the block either.
 */

export const MANUAL_IHI_VALIDATION_REQUIREMENT_ID = 'HI_010_005819';

export const VERIFIED_IHI_OUTCOME = 'verified_candidate_match';

const DISCARD_IHI_OUTCOMES = [
	'discard_invalid_mismatch',
	'discard_no_match',
	'discard_ambiguous_result',
	'discard_unsupported_provisional',
	'discard_unsupported_unverified',
	'discard_unsupported_status',
	'discard_malformed_result',
	'discard_validation_unavailable',
	'discard_indeterminate',
] as const;

export type IhiDiscardOutcome = (typeof DISCARD_IHI_OUTCOMES)[number];
export type IhiValidationOutcome = typeof VERIFIED_IHI_OUTCOME | IhiDiscardOutcome;

// The three fixed server messages. The message is part of the bounded contract:
// a block whose message is not exactly one of these is treated as malformed, so
// free-text provider/fault/error content can never reach the operator as IHI
// feedback.
export const IHI_VERIFIED_MESSAGE = 'The supplied IHI was validated and has been stored.';
export const IHI_DISCARDED_SAVED_MESSAGE =
	'The supplied IHI could not be validated and has been discarded. The patient record has been saved without it.';
export const IHI_DISCARDED_NOT_SAVED_MESSAGE =
	'The supplied IHI could not be validated and has been discarded. An IHI is mandatory for patient registration in the current legislative/configuration mode, so the patient record has not been saved.';

export interface IhiValidationFeedback {
	requirement_id: typeof MANUAL_IHI_VALIDATION_REQUIREMENT_ID;
	outcome: IhiValidationOutcome;
	operator_alert: boolean;
	candidate_discarded: boolean;
	message: string;
}

const isDiscardOutcome = (outcome: unknown): outcome is IhiDiscardOutcome =>
	(DISCARD_IHI_OUTCOMES as readonly unknown[]).includes(outcome);

/**
 * Validate an `ihi_validation` response value against the bounded contract.
 *
 * Returns a freshly built object containing only the five contract fields, or
 * `null` when the value is absent, malformed, internally inconsistent or outside
 * the fixed vocabulary. `null` means "no trusted evaluated outcome": show no
 * IHI-specific feedback and trigger no field reconciliation.
 */
export const parseIhiValidationFeedback = (value: unknown): IhiValidationFeedback | null => {
	if (typeof value !== 'object' || value === null || Array.isArray(value)) {
		return null;
	}

	const { requirement_id, outcome, operator_alert, candidate_discarded, message } =
		value as Record<string, unknown>;

	if (requirement_id !== MANUAL_IHI_VALIDATION_REQUIREMENT_ID) return null;
	if (typeof operator_alert !== 'boolean' || typeof candidate_discarded !== 'boolean') return null;
	if (typeof message !== 'string') return null;

	if (outcome === VERIFIED_IHI_OUTCOME) {
		// A verified result never alerts or discards and carries the fixed
		// verified message; any contradiction means the block is not trustworthy.
		if (operator_alert || candidate_discarded) return null;
		if (message !== IHI_VERIFIED_MESSAGE) return null;
	} else if (isDiscardOutcome(outcome)) {
		// Every discard outcome alerts and discards, with one of the two fixed
		// discard messages (patient saved without the candidate, or not saved).
		if (!operator_alert || !candidate_discarded) return null;
		if (message !== IHI_DISCARDED_SAVED_MESSAGE && message !== IHI_DISCARDED_NOT_SAVED_MESSAGE) return null;
	} else {
		return null;
	}

	return {
		requirement_id: MANUAL_IHI_VALIDATION_REQUIREMENT_ID,
		outcome,
		operator_alert,
		candidate_discarded,
		message,
	};
};

/**
 * Safely extract the bounded `ihi_validation` block from a failed patient
 * mutation, before ordinary error handling. Only a 422 response body is
 * consulted (the mandatory-IHI registration rejection); anything else — network
 * errors, other statuses, missing or malformed bodies — returns `null` and
 * leaves ordinary error handling untouched. Structural checks only: nothing is
 * thrown and nothing is logged.
 */
export const extractIhiValidationFromMutationError = (error: unknown): IhiValidationFeedback | null => {
	if (typeof error !== 'object' || error === null) return null;

	const response = (error as { response?: unknown }).response;
	if (typeof response !== 'object' || response === null) return null;

	const { status, data } = response as { status?: unknown; data?: unknown };
	if (status !== 422) return null;
	if (typeof data !== 'object' || data === null) return null;

	return parseIhiValidationFeedback((data as { ihi_validation?: unknown }).ihi_validation);
};
